import { useEffect, useRef, useState } from 'react'
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  PackageCheck,
  PackagePlus,
  Search,
  SendHorizonal,
  ShoppingCart,
  User2,
  X,
} from 'lucide-react'
import { Button, Input, Modal, useToast } from '@/components/ui'
import { cn } from '@/lib/utils.ts'
import { apiClient } from '@/lib/api.ts'
import {
  exportQuoteStep,
  fetchQuotePositions,
  updateQuote,
  updateQuotePosition,
  QuoteExportValidationError,
  type Quote,
  type QuotePosition,
  type ExportSapResult,
  type ExportValidationError,
} from '@/api/quotes.ts'
import { StepForm } from './SapArticleCreationWizard.tsx'

// ─── Types ───────────────────────────────────────────────────────────────────

interface CustomerResult {
  id: string
  name: string
  source: 'sap' | 'tanss'
  sapBpCode?: string
  tenantId?: string
  tenantCode?: string
}

// ─── Wizard phase constants ───────────────────────────────────────────────────

type Phase = 'customer' | 'articles' | `article_${string}` | 'ek_prices' | 'export' | 'done'

// ─── Step indicator ───────────────────────────────────────────────────────────

function StepIndicator({
  labels,
  activeIndex,
}: {
  labels: string[]
  activeIndex: number
}) {
  return (
    <div className="flex items-center gap-1 px-5 pb-4 pt-2 border-b border-gray-100">
      {labels.map((label, i) => {
        const done = i < activeIndex
        const active = i === activeIndex
        return (
          <div key={i} className="flex items-center gap-1 min-w-0">
            {i > 0 && (
              <div
                className={cn('h-px flex-1 min-w-[12px]', done ? 'bg-brand-500' : 'bg-gray-200')}
              />
            )}
            <div className="flex items-center gap-1.5 shrink-0">
              <div
                className={cn(
                  'w-6 h-6 rounded-full flex items-center justify-center text-caption font-semibold',
                  done && 'bg-brand-500 text-white',
                  active && 'bg-brand-600 text-white ring-2 ring-brand-200',
                  !done && !active && 'bg-gray-100 text-gray-400',
                )}
              >
                {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : i + 1}
              </div>
              <span
                className={cn(
                  'text-caption truncate max-w-[80px]',
                  active ? 'font-semibold text-brand-700' : done ? 'text-gray-500' : 'text-gray-400',
                )}
              >
                {label}
              </span>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ─── Customer step ────────────────────────────────────────────────────────────

function CustomerStep({
  quote,
  tenantId,
  onCustomerConfirmed,
  onNext,
}: {
  quote: Quote
  tenantId?: string | null
  onCustomerConfirmed: (updated: Quote) => void
  onNext: () => void
}) {
  const [editing, setEditing] = useState(!quote.customerSapBpCode)
  const [query, setQuery] = useState(quote.customerName ?? '')
  const [suggestions, setSuggestions] = useState<CustomerResult[]>([])
  const [selected, setSelected] = useState<CustomerResult | null>(null)
  const [searchLoading, setSearchLoading] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const hasValidCustomer = !!quote.customerSapBpCode

  useEffect(() => {
    if (!editing) return
    setTimeout(() => inputRef.current?.focus(), 50)
  }, [editing])

  useEffect(() => {
    if (!editing || selected) return
    if (query.trim().length < 2) {
      setSuggestions([])
      setShowSuggestions(false)
      return
    }
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      setSearchLoading(true)
      try {
        const { data } = await apiClient.get('/quotes/customers/search', {
          params: { q: query, ...(tenantId ? { tenantId } : {}) },
        })
        setSuggestions(Array.isArray(data) ? data : [])
        setShowSuggestions(true)
      } catch {
        setSuggestions([])
        setShowSuggestions(false)
      } finally {
        setSearchLoading(false)
      }
    }, 300)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [query, selected, editing, tenantId])

  async function handleSaveCustomer() {
    if (!selected) return
    setSaving(true)
    setSaveError(null)
    try {
      const updated = await updateQuote(quote.id, {
        customerSapBpCode: selected.sapBpCode ?? null,
        customerTanssId: selected.source === 'tanss' ? selected.id : null,
        customerName: selected.name,
        ...(selected.tenantId != null ? { tenantId: selected.tenantId } : {}),
      })
      onCustomerConfirmed(updated)
      setEditing(false)
      setShowSuggestions(false)
    } catch (err) {
      const data = (err as { response?: { data?: { error?: string } } })?.response?.data
      setSaveError(data?.error ?? (err instanceof Error ? err.message : 'Kunde konnte nicht gespeichert werden'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="px-5 py-4 space-y-5">
      {/* Current customer card */}
      {!editing && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="mt-0.5 w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center shrink-0">
              <User2 className="w-4 h-4 text-brand-600" />
            </div>
            <div className="min-w-0">
              <p className="text-body font-semibold text-gray-900 truncate">
                {quote.customerName ?? '—'}
              </p>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                {quote.customerSapBpCode && (
                  <span className="inline-flex items-center gap-1 text-caption text-gray-500">
                    <Building2 className="w-3 h-3" /> SAP: {quote.customerSapBpCode}
                  </span>
                )}
              </div>
              {!hasValidCustomer && (
                <p className="mt-1 text-caption text-amber-600 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Kein SAP-Geschäftspartner — bitte Kunden wählen
                </p>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="xs"
            onClick={() => setEditing(true)}
          >
            Ändern
          </Button>
        </div>
      )}

      {/* Customer search (shown when editing) */}
      {editing && (
        <div className="space-y-3">
          <label className="block text-small font-semibold text-gray-700">
            Kunden suchen
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => {
                if (selected) { setSelected(null); setShowSuggestions(false) }
                setQuery(e.target.value)
              }}
              placeholder="Kundenname oder Kundennummer suchen…"
              className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-md text-body focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              autoComplete="off"
              disabled={saving}
            />
            {(query || selected) && !saving && (
              <button
                type="button"
                onClick={() => { setSelected(null); setQuery(''); setSuggestions([]); setShowSuggestions(false) }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label="Auswahl löschen"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {showSuggestions && (
            <ul className="w-full bg-white border border-gray-200 rounded-md shadow-md max-h-48 overflow-auto">
              {searchLoading ? (
                <li className="px-3 py-2 text-small text-gray-500">Suche…</li>
              ) : suggestions.length === 0 ? (
                <li className="px-3 py-2 text-small text-gray-500">Keine Treffer</li>
              ) : (
                suggestions.map((p) => (
                  <li key={`${p.source}-${p.id}`}>
                    <button
                      type="button"
                      onClick={() => { setSelected(p); setQuery(p.name); setShowSuggestions(false); setSuggestions([]) }}
                      className="w-full text-left px-3 py-2 text-small hover:bg-brand-50 focus:outline-none focus:bg-brand-50"
                    >
                      <span className="font-medium text-gray-900">{p.name}</span>
                      {p.sapBpCode && <span className="ml-2 text-gray-400">{p.sapBpCode}</span>}
                      {p.tenantCode && <span className="ml-2 text-caption text-brand-600 font-medium">{p.tenantCode}</span>}
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}

          {selected && (
            <p className="text-caption text-brand-700">
              ✓ {selected.name}{selected.sapBpCode && ` (${selected.sapBpCode})`}
            </p>
          )}

          {saveError && (
            <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-small text-red-700">
              {saveError}
            </div>
          )}

          <div className="flex gap-2">
            {hasValidCustomer && (
              <Button variant="ghost" size="sm" onClick={() => { setEditing(false); setSelected(null); setQuery(quote.customerName ?? '') }}>
                Abbrechen
              </Button>
            )}
            <Button
              variant="primary"
              size="sm"
              onClick={handleSaveCustomer}
              disabled={!selected || saving}
              loading={saving}
            >
              Kunden übernehmen
            </Button>
          </div>
        </div>
      )}

      <div className="flex justify-end pt-2">
        <Button
          variant="primary"
          size="sm"
          trailingIcon={<ChevronRight className="w-4 h-4" />}
          onClick={onNext}
          disabled={!hasValidCustomer}
        >
          Weiter
        </Button>
      </div>
    </div>
  )
}

// ─── Articles overview step ───────────────────────────────────────────────────

function ArticlesOverviewStep({
  positions,
  onNext,
  onBack,
}: {
  positions: QuotePosition[]
  onNext: () => void
  onBack: () => void
}) {
  const missing = positions.filter((p) => p.kind !== 'note' && p.kind !== 'subtotal' && (!p.sapItemCode || p.needsSapCreation))
  const withCode = positions.filter((p) => p.kind !== 'note' && p.kind !== 'subtotal' && !!p.sapItemCode && !p.needsSapCreation)

  return (
    <div className="px-5 py-4 space-y-4">
      {missing.length === 0 ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-4 flex items-start gap-3">
          <PackageCheck className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-body font-semibold text-emerald-800">Alle Artikel bereits in SAP vorhanden</p>
            <p className="text-small text-emerald-700 mt-0.5">
              {withCode.length} Position(en) mit SAP-Artikelcode — direkt mit der Übergabe fortfahren.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3">
            <PackagePlus className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-body font-semibold text-amber-800">
                {missing.length} Artikel noch nicht in SAP angelegt
              </p>
              <p className="text-small text-amber-700 mt-0.5">
                Im nächsten Schritt kannst du jeden Artikel einzeln definieren und anlegen.
              </p>
            </div>
          </div>

          <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 overflow-hidden">
            {missing.map((p, i) => (
              <li key={p.id} className="flex items-center gap-3 px-4 py-2.5 bg-white">
                <span className="text-caption text-gray-400 w-5 text-right shrink-0">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-small font-medium text-gray-800 truncate">{p.description}</p>
                  <p className="text-caption text-gray-500">
                    Menge: {Number(p.quantity)}
                    {p.unitPrice ? ` · ${Number(p.unitPrice).toFixed(2)} €` : ''}
                    {' · '}{p.kind === 'service' ? 'Dienstleistung' : 'Artikel'}
                  </p>
                </div>
                <span className="text-caption text-amber-600 font-medium shrink-0">fehlt</span>
              </li>
            ))}
          </ul>

          {withCode.length > 0 && (
            <p className="text-caption text-gray-500">
              {withCode.length} weitere Position(en) mit SAP-Code bereits zugewiesen.
            </p>
          )}
        </div>
      )}

      <div className="flex justify-between pt-2">
        <Button
          variant="ghost"
          size="sm"
          leadingIcon={<ChevronLeft className="w-4 h-4" />}
          onClick={onBack}
        >
          Zurück
        </Button>
        <Button
          variant="primary"
          size="sm"
          trailingIcon={<ChevronRight className="w-4 h-4" />}
          onClick={onNext}
        >
          {missing.length === 0 ? 'Zur Übergabe' : `${missing.length} Artikel anlegen`}
        </Button>
      </div>
    </div>
  )
}

// ─── Single article creation step ────────────────────────────────────────────

function ArticleCreateStep({
  position,
  articleIndex,
  totalArticles,
  resolvedCount,
  tenantId,
  quoteId,
  onCreated,
  onSkip,
  onBack,
}: {
  position: QuotePosition
  articleIndex: number
  totalArticles: number
  resolvedCount: number
  tenantId?: string | null
  quoteId: string
  onCreated: (updated: QuotePosition) => void
  onSkip: () => void
  onBack: () => void
}) {
  const { toast } = useToast()

  async function handleCreated(itemCode: string, _itemName: string, salesPrice?: number, purchasePrice?: number) {
    try {
      const patch: Record<string, unknown> = { sapItemCode: itemCode }
      if (!position.unitPrice && salesPrice && salesPrice > 0) patch.unitPrice = salesPrice
      if (purchasePrice != null && purchasePrice > 0) patch.purchasePrice = purchasePrice
      const updated = await updateQuotePosition(quoteId, position.id, patch)
      onCreated(updated)
    } catch (err) {
      toast({
        title: `Zuweisung fehlgeschlagen: ${err instanceof Error ? err.message : 'Fehler'}`,
        tone: 'danger',
      })
    }
  }

  return (
    <div className="px-5 py-4 space-y-3">
      {/* Progress bar */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
          <div
            className="h-full bg-brand-500 transition-all duration-300"
            style={{ width: `${totalArticles > 0 ? (resolvedCount / totalArticles) * 100 : 0}%` }}
          />
        </div>
        <span className="text-caption text-gray-500 whitespace-nowrap shrink-0">
          {resolvedCount}/{totalArticles} angelegt
        </span>
      </div>

      {/* Position info */}
      <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3">
        <p className="text-body font-medium text-gray-900">{position.description}</p>
        <p className="text-caption text-gray-500">
          Menge: {Number(position.quantity)}
          {position.unitPrice ? ` · Einzelpreis: ${Number(position.unitPrice).toFixed(2)} €` : ''}
          {' · '}{position.kind === 'service' ? 'Dienstleistung' : position.kind === 'subtotal' ? 'Zwischensumme' : 'Artikel'}
        </p>
      </div>

      <StepForm
        key={position.id}
        position={position}
        tenantId={tenantId}
        onCreated={handleCreated}
        onSkip={onSkip}
      />

      <div className="flex justify-start pt-1">
        <Button
          variant="ghost"
          size="sm"
          leadingIcon={<ChevronLeft className="w-4 h-4" />}
          onClick={onBack}
          disabled={articleIndex === 0}
        >
          Zurück
        </Button>
      </div>
    </div>
  )
}

// ─── EK-Preise step ──────────────────────────────────────────────────────────

function EkPriceStep({
  positions,
  quoteId,
  onSaved,
  onBack,
}: {
  positions: QuotePosition[]
  quoteId: string
  onSaved: (updated: QuotePosition[]) => void
  onBack: () => void
}) {
  const { toast } = useToast()
  const [ekValues, setEkValues] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)

  async function handleSave() {
    setSaving(true)
    try {
      const updates: QuotePosition[] = []
      for (const pos of positions) {
        const val = ekValues[pos.id]
        if (val && Number(val) > 0) {
          const updated = await updateQuotePosition(quoteId, pos.id, { purchasePrice: Number(val) })
          updates.push(updated)
        }
      }
      onSaved(updates)
    } catch (err) {
      toast({
        title: `EK-Preise konnten nicht gespeichert werden: ${err instanceof Error ? err.message : 'Fehler'}`,
        tone: 'danger',
      })
      setSaving(false)
    }
  }

  return (
    <div className="px-5 py-4 space-y-4">
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3">
        <ShoppingCart className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
        <div>
          <p className="text-body font-semibold text-amber-800">
            {positions.length} Position{positions.length !== 1 ? 'en' : ''} ohne Einkaufspreis
          </p>
          <p className="text-small text-amber-700 mt-0.5">
            Optional: Einkaufspreise eintragen oder leer lassen, um fortzufahren.
          </p>
        </div>
      </div>

      <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 overflow-hidden">
        {positions.map((p) => (
          <li key={p.id} className="flex items-center gap-3 px-4 py-2.5 bg-white">
            <div className="min-w-0 flex-1">
              <p className="text-small font-medium text-gray-800 truncate">{p.description}</p>
              <p className="text-caption text-gray-500">
                {p.sapItemCode} · Menge: {Number(p.quantity)}
                {p.unitPrice ? ` · VK: ${Number(p.unitPrice).toFixed(2)} €` : ''}
              </p>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="EK"
                value={ekValues[p.id] ?? ''}
                onChange={(e) => setEkValues((prev) => ({ ...prev, [p.id]: e.target.value }))}
                className="w-24 text-right"
                disabled={saving}
              />
              <span className="text-caption text-gray-500">€</span>
            </div>
          </li>
        ))}
      </ul>

      <div className="flex justify-between pt-2">
        <Button
          variant="ghost"
          size="sm"
          leadingIcon={<ChevronLeft className="w-4 h-4" />}
          onClick={onBack}
          disabled={saving}
        >
          Zurück
        </Button>
        <Button
          variant="primary"
          size="sm"
          trailingIcon={<ChevronRight className="w-4 h-4" />}
          onClick={handleSave}
          loading={saving}
        >
          Weiter
        </Button>
      </div>
    </div>
  )
}

// ─── Export/confirmation step ─────────────────────────────────────────────────

type ExportPhase = 'idle' | 'building' | 'creating' | 'saving'

const PHASE_LABELS: Record<ExportPhase, string> = {
  idle: '',
  building: 'Angebot wird vorbereitet…',
  creating: 'Angebot wird in SAP angelegt…',
  saving: 'Ergebnis wird gespeichert…',
}

function ExportStep({
  quote,
  positions,
  onBack,
  onExportSuccess,
}: {
  quote: Quote
  positions: QuotePosition[]
  onBack: () => void
  onExportSuccess: (result: ExportSapResult) => void
}) {
  const [phase, setPhase] = useState<ExportPhase>('idle')
  const [error, setError] = useState<string | null>(null)
  const [validationErrors, setValidationErrors] = useState<ExportValidationError[] | null>(null)

  const exporting = phase !== 'idle'
  const articlePositions = positions.filter((p) => p.kind !== 'note' && p.kind !== 'subtotal')
  const withCode = articlePositions.filter((p) => !!p.sapItemCode && !p.needsSapCreation)
  const stillMissing = articlePositions.filter((p) => !p.sapItemCode || p.needsSapCreation)

  async function handleExport() {
    setPhase('building')
    setError(null)
    setValidationErrors(null)
    try {
      // Step 1: validate + build SAP body (DB-only, fast)
      const buildResult = await exportQuoteStep(quote.id, 'build-body')
      if (buildResult.errors && buildResult.errors.length > 0) {
        setValidationErrors(buildResult.errors)
        return
      }
      if (buildResult.error || !buildResult.body) {
        setError(buildResult.error ?? 'Fehler beim Aufbau des SAP-Exports')
        return
      }

      // Step 2: POST /Quotations to SAP (single bounded HTTP request)
      setPhase('creating')
      const createResult = await exportQuoteStep(quote.id, 'create', { body: buildResult.body })
      if (createResult.error) {
        setError(createResult.error)
        return
      }
      if (createResult.docEntry == null || createResult.docNum == null) {
        setError('SAP hat keine Belegnummer zurückgegeben')
        return
      }

      // Step 3: write docEntry/docNum back to DB (fast)
      setPhase('saving')
      const saveResult = await exportQuoteStep(quote.id, 'save-back', {
        docEntry: createResult.docEntry,
        docNum: createResult.docNum,
      })
      if (!saveResult.ok) {
        setError('SAP-Export erfolgreich, aber lokale Speicherung fehlgeschlagen')
        return
      }

      onExportSuccess({ docEntry: createResult.docEntry, docNum: createResult.docNum, updated: createResult.updated })
    } catch (err) {
      if (err instanceof QuoteExportValidationError) {
        setValidationErrors(err.errors)
      } else {
        setError(err instanceof Error ? err.message : 'Unbekannter Fehler beim SAP-Export')
      }
    } finally {
      setPhase('idle')
    }
  }

  return (
    <div className="px-5 py-4 space-y-4">
      <div className="space-y-3">
        <h3 className="text-body font-semibold text-gray-900">Zusammenfassung</h3>

        {/* Customer */}
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 flex items-center gap-3">
          <User2 className="w-4 h-4 text-gray-500 shrink-0" />
          <div className="min-w-0">
            <p className="text-small font-medium text-gray-800">{quote.customerName ?? '—'}</p>
            {quote.customerSapBpCode && (
              <p className="text-caption text-gray-500">SAP-Code: {quote.customerSapBpCode}</p>
            )}
          </div>
        </div>

        {/* Article summary */}
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 space-y-1">
          <div className="flex items-center gap-2">
            <PackageCheck className="w-4 h-4 text-emerald-600 shrink-0" />
            <p className="text-small text-gray-700">
              <span className="font-semibold text-gray-900">{withCode.length}</span> Artikel mit SAP-Code
            </p>
          </div>
          {stillMissing.length > 0 && (
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
              <p className="text-small text-red-700">
                <span className="font-semibold">{stillMissing.length}</span> Artikel ohne SAP-Code — Übergabe blockiert. Bitte zurück zu "Artikel" und alle Codes zuweisen.
              </p>
            </div>
          )}
        </div>

        {exporting && (
          <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 flex items-center gap-2 text-small text-blue-700">
            <span className="inline-block w-3.5 h-3.5 border-2 border-blue-400 border-t-blue-700 rounded-full animate-spin shrink-0" />
            <span>{PHASE_LABELS[phase]}</span>
          </div>
        )}

        {error && (
          <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-small text-red-700">
            <p className="font-medium">Fehler beim SAP-Export</p>
            <p className="mt-0.5">{error}</p>
          </div>
        )}

        {validationErrors && validationErrors.length > 0 && (
          <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 space-y-1">
            <p className="text-small font-medium text-amber-800">Übergabe nicht möglich:</p>
            <ul className="space-y-0.5">
              {validationErrors.map((e, i) => (
                <li key={i} className="text-small text-amber-700">• {e.message}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="flex justify-between pt-2">
        <Button
          variant="ghost"
          size="sm"
          leadingIcon={<ChevronLeft className="w-4 h-4" />}
          onClick={onBack}
          disabled={exporting}
        >
          Zurück
        </Button>
        <Button
          variant="primary"
          size="sm"
          leadingIcon={<SendHorizonal className="w-4 h-4" />}
          onClick={handleExport}
          loading={exporting}
          disabled={exporting || stillMissing.length > 0}
        >
          Jetzt an SAP übergeben
        </Button>
      </div>
    </div>
  )
}

// ─── Done step ────────────────────────────────────────────────────────────────

function DoneStep({
  result,
  onClose,
}: {
  result: ExportSapResult
  onClose: () => void
}) {
  const tenantLabel = result.tenant?.code
    ? ` · Mandant ${result.tenant.code}${result.tenant.name ? ` · ${result.tenant.name}` : ''}`
    : ''

  return (
    <div className="px-5 py-8 text-center space-y-4">
      <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
        <CheckCircle2 className="w-8 h-8 text-emerald-600" />
      </div>
      <div>
        <p className="text-h3 font-bold text-gray-900">{result.updated ? 'Angebot aktualisiert!' : 'Angebot angelegt!'}</p>
        <p className="text-body text-gray-600 mt-1">
          SAP-Angebot <span className="font-semibold text-brand-700">QU-{result.docNum}</span> wurde erfolgreich in SAP {result.updated ? 'aktualisiert' : 'erstellt'}{tenantLabel}.
        </p>
      </div>
      <Button variant="primary" onClick={onClose}>
        Fertig
      </Button>
    </div>
  )
}

// ─── Main wizard ──────────────────────────────────────────────────────────────

interface SapTransferWizardModalProps {
  open: boolean
  quote: Quote
  positions: QuotePosition[]
  onClose: () => void
  onExportSuccess: (result: ExportSapResult) => void
  onPositionUpdated: (updated: QuotePosition) => void
  onQuoteUpdated: (updated: Quote) => void
}

export function SapTransferWizardModal({
  open,
  quote: initialQuote,
  positions: initialPositions,
  onClose,
  onExportSuccess,
  onPositionUpdated,
  onQuoteUpdated,
}: SapTransferWizardModalProps) {
  const [quote, setQuote] = useState(initialQuote)
  const [positions, setPositions] = useState(initialPositions)
  const [phase, setPhase] = useState<Phase>('customer')
  const [resolvedIds, setResolvedIds] = useState<Set<string>>(new Set())
  const [exportResult, setExportResult] = useState<ExportSapResult | null>(null)

  // Sync external changes
  useEffect(() => { setQuote(initialQuote) }, [initialQuote])
  useEffect(() => { setPositions(initialPositions) }, [initialPositions])

  // Reset wizard when opened
  useEffect(() => {
    if (open) {
      setPhase('customer')
      setResolvedIds(new Set())
      setExportResult(null)
    }
  }, [open])

  // Articles that need SAP creation
  const missingArticles = positions.filter((p) => p.kind !== 'note' && p.kind !== 'subtotal' && (!p.sapItemCode || p.needsSapCreation))

  // Positions with SAP code but no purchase price
  const positionsNeedingEk = positions.filter(
    (p) => p.kind !== 'note' && p.kind !== 'subtotal' && !!p.sapItemCode && !p.needsSapCreation && (!p.purchasePrice || Number(p.purchasePrice) <= 0),
  )
  const hasEkPhase = positionsNeedingEk.length > 0

  // Build step labels for indicator
  const articleLabels = missingArticles.map((_, i) => `Artikel ${i + 1}`)
  const stepLabels = ['Kunde', 'Artikel', ...articleLabels, ...(hasEkPhase ? ['EK-Preise'] : []), 'Übergabe']

  function phaseToStepIndex(p: Phase): number {
    if (p === 'customer') return 0
    if (p === 'articles') return 1
    if (p === 'ek_prices') return 2 + missingArticles.length
    if (p === 'export' || p === 'done') return stepLabels.length - 1
    if (p.startsWith('article_')) {
      const posId = p.slice('article_'.length)
      const idx = missingArticles.findIndex((pos) => pos.id === posId)
      if (idx !== -1) return 2 + idx
    }
    return 0
  }

  async function handleQuoteUpdated(updated: Quote) {
    const prevTenantId = quote.tenantId
    setQuote(updated)
    onQuoteUpdated(updated)
    // When the SAP Mandant changes the backend resets needsSapCreation on all
    // positions. Re-fetch so ArticlesOverviewStep shows the correct state
    // (instead of stale "already in SAP" for codes that don't exist in the new tenant).
    if (updated.tenantId !== prevTenantId) {
      try {
        const fresh = await fetchQuotePositions(updated.id)
        setPositions(fresh)
        for (const p of fresh) onPositionUpdated(p)
      } catch {
        // If the reload fails the wizard stays usable; the export-step
        // SAP pre-flight will catch missing codes as validation errors.
      }
    }
  }

  function handlePositionCreated(updated: QuotePosition) {
    const updatedPositions = positions.map((p) => (p.id === updated.id ? updated : p))
    setPositions(updatedPositions)
    const newResolvedIds = new Set(resolvedIds)
    newResolvedIds.add(updated.id)
    setResolvedIds(newResolvedIds)
    onPositionUpdated(updated)
    // Find next unresolved article (using updated resolvedIds to avoid the race)
    const next = missingArticles.find((p) => !newResolvedIds.has(p.id))
    if (next) {
      setPhase(`article_${next.id}`)
    } else {
      // Check updated positions for missing EK prices
      const stillNeedsEk = updatedPositions.some(
        (p) => p.kind !== 'note' && p.kind !== 'subtotal' && !!p.sapItemCode && !p.needsSapCreation && (!p.purchasePrice || Number(p.purchasePrice) <= 0),
      )
      setPhase(stillNeedsEk ? 'ek_prices' : 'export')
    }
  }

  function handleEkSaved(updated: QuotePosition[]) {
    setPositions((prev) => prev.map((p) => updated.find((u) => u.id === p.id) ?? p))
    for (const u of updated) onPositionUpdated(u)
    setPhase('export')
  }

  function handleArticleSkip(posId: string) {
    const idx = missingArticles.findIndex((p) => p.id === posId)
    const nextIdx = idx + 1
    if (nextIdx < missingArticles.length) {
      setPhase(`article_${missingArticles[nextIdx]!.id}`)
    } else {
      setPhase(hasEkPhase ? 'ek_prices' : 'export')
    }
  }

  function handleExportSuccess(result: ExportSapResult) {
    setExportResult(result)
    setPhase('done')
    onExportSuccess(result)
  }

  function handleClose() {
    onClose()
  }

  const activeStepIndex = phaseToStepIndex(phase)

  function getBackPhase(): Phase {
    if (phase === 'articles') return 'customer'
    if (phase === 'ek_prices') {
      if (missingArticles.length > 0) return `article_${missingArticles[missingArticles.length - 1]!.id}`
      return 'articles'
    }
    if (phase === 'export') {
      if (hasEkPhase) return 'ek_prices'
      if (missingArticles.length > 0) return `article_${missingArticles[missingArticles.length - 1]!.id}`
      return 'articles'
    }
    if (phase.startsWith('article_')) {
      const posId = phase.slice('article_'.length)
      const idx = missingArticles.findIndex((p) => p.id === posId)
      if (idx === 0) return 'articles'
      if (idx > 0) return `article_${missingArticles[idx - 1]!.id}`
    }
    return 'customer'
  }

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) handleClose() }}
      title="SAP-Übergabe"
      description="Geführte Übergabe des Angebots an SAP"
      size="xl"
    >
      {phase !== 'done' && (
        <StepIndicator labels={stepLabels} activeIndex={activeStepIndex} />
      )}

      <div className="max-h-[75vh] overflow-y-auto">
        {phase === 'customer' && (
          <CustomerStep
            quote={quote}
            tenantId={quote.tenantId}
            onCustomerConfirmed={handleQuoteUpdated}
            onNext={() => setPhase('articles')}
          />
        )}

        {phase === 'articles' && (
          <ArticlesOverviewStep
            positions={positions}
            onNext={() => {
              if (missingArticles.length > 0) {
                setPhase(`article_${missingArticles[0]!.id}`)
              } else if (hasEkPhase) {
                setPhase('ek_prices')
              } else {
                setPhase('export')
              }
            }}
            onBack={() => setPhase('customer')}
          />
        )}

        {missingArticles.map((pos, idx) => (
          phase === `article_${pos.id}` && (
            <ArticleCreateStep
              key={pos.id}
              position={pos}
              articleIndex={idx}
              totalArticles={missingArticles.length}
              resolvedCount={resolvedIds.size}
              tenantId={quote.tenantId}
              quoteId={quote.id}
              onCreated={handlePositionCreated}
              onSkip={() => handleArticleSkip(pos.id)}
              onBack={() => setPhase(getBackPhase())}
            />
          )
        ))}

        {phase === 'ek_prices' && (
          <EkPriceStep
            positions={positionsNeedingEk}
            quoteId={quote.id}
            onSaved={handleEkSaved}
            onBack={() => setPhase(getBackPhase())}
          />
        )}

        {phase === 'export' && (
          <ExportStep
            quote={quote}
            positions={positions}
            onBack={() => setPhase(getBackPhase())}
            onExportSuccess={handleExportSuccess}
          />
        )}

        {phase === 'done' && exportResult && (
          <DoneStep
            result={exportResult}
            onClose={handleClose}
          />
        )}
      </div>
    </Modal>
  )
}
